// SPDX-License-Identifier: AGPL-3.0-or-later
// WebRTC carries microphone/speaker audio; authenticated Konvo routes own the user's Codex process.
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {LiveCallbacks, LiveState} from './GeminiLiveEngine';

type VoiceEvent = {seq: number; type: string; role?: string; text?: string; done?: boolean; status?: string};
interface StartReply {
	id: string;
	sdp: string;
	voice: string;
}
export interface CodexVoiceCallbacks extends LiveCallbacks {
	mode: 'codex-live' | 'codex-voice';
	channelId: string;
	context?: () => unknown;
	sendDelay: number;
	onUserDone?: (text: string) => void;
}
async function microphonePermission(): Promise<PermissionState | null> {
	try {
		return (await navigator.permissions?.query({name: 'microphone' as PermissionName}))?.state ?? null;
	} catch {
		return null;
	}
}
export class CodexLiveEngine {
	readonly audio: {context: AudioContext | null} = {context: null};
	private peer: RTCPeerConnection | null = null;
	private channel: RTCDataChannel | null = null;
	private stream: MediaStream | null = null;
	private mic: AnalyserNode | null = null;
	private output: AnalyserNode | null = null;
	private gain: GainNode | null = null;
	private remoteAudio: HTMLAudioElement | null = null;
	private id: string | null = null;
	private closed = false;
	private started = false;
	private cursor = 0;
	private pollTimer: ReturnType<typeof setTimeout> | null = null;
	private ticker: ReturnType<typeof setInterval> | null = null;
	private backendPending = false;
	private waitingForAudio = false;
	private answerAudioSeen = false;
	private answerReadyAt = 0;
	private userDone = false;
	private assistantDone = false;
	private interrupted = false;
	private lastOutput = Number.NEGATIVE_INFINITY;
	private lastInput = 0;
	private state: LiveState = 'listening';
	private userText = '';
	private assistantText = '';
	private readonly samples = new Float32Array(1024);
	constructor(
		private readonly callbacks: CodexVoiceCallbacks,
		private readonly primedContext?: AudioContext | null,
	) {}
	async start(): Promise<void> {
		if (
			!navigator.mediaDevices?.getUserMedia ||
			typeof RTCPeerConnection === 'undefined' ||
			typeof AudioContext === 'undefined'
		)
			throw new Error('unsupported');
		const context = this.primedContext ?? new AudioContext();
		this.audio.context = context;
		void context.resume().catch(() => {});
		try {
			const peer = new RTCPeerConnection();
			this.peer = peer;
			this.mic = context.createAnalyser();
			this.mic.fftSize = 1024;
			this.output = context.createAnalyser();
			this.output.fftSize = 1024;
			// Keep the analysers running silently; the media element owns WebRTC playback.
			this.gain = context.createGain();
			this.gain.gain.value = 0;
			this.mic.connect(this.gain);
			this.output.connect(this.gain);
			this.gain.connect(context.destination);
			peer.ontrack = (event) => {
				if (!this.closed) {
					const remote = event.streams[0] ?? new MediaStream([event.track]);
					context.createMediaStreamSource(remote).connect(this.output!);
					const audio = new Audio();
					audio.autoplay = true;
					audio.srcObject = remote;
					this.remoteAudio = audio;
					void audio.play().catch(() => {
						if (!this.closed) {
							this.callbacks.onError('connection');
							this.close();
						}
					});
				}
			};
			peer.onconnectionstatechange = () => {
				if (this.started && !this.closed && ['failed', 'closed'].includes(peer.connectionState)) {
					this.callbacks.onError('connection');
					this.close();
				}
			};
			// The microphone joins this sender once it opens, so the session is negotiated meanwhile.
			const sender = peer.addTransceiver('audio', {direction: 'sendrecv'}).sender;
			const channel = peer.createDataChannel('oai-events');
			this.channel = channel;
			channel.onmessage = (event) => {
				try {
					this.mediaEvent(JSON.parse(event.data));
				} catch {
					/* Unknown experimental event. */
				}
			};
			const offer = await peer.createOffer();
			await peer.setLocalDescription(offer);
			// A refused microphone is known up front: say so without starting a session.
			if ((await microphonePermission()) === 'denied') await this.openMic(sender);
			const microphone = this.openMic(sender);
			const answer = this.requestSession(peer.localDescription?.sdp);
			void answer.catch(() => {});
			await microphone;
			await peer.setRemoteDescription({type: 'answer', sdp: await answer});
			await new Promise<void>((resolve, reject) => {
				const timeout = setTimeout(() => done(new Error('Codex audio connection timed out')), 25_000);
				const done = (error?: Error) => {
					clearTimeout(timeout);
					channel.removeEventListener('open', onOpen);
					channel.removeEventListener('close', onClose);
					error ? reject(error) : resolve();
				};
				const onOpen = () => done();
				const onClose = () => done(new Error('Codex audio connection closed'));
				channel.addEventListener('open', onOpen, {once: true});
				channel.addEventListener('close', onClose, {once: true});
				if (channel.readyState === 'open') done();
			});
			if (this.closed) throw new Error('closed');
			this.started = true;
			this.callbacks.onState('listening');
			this.ticker = setInterval(() => this.tick(), 50);
			void this.poll();
		} catch (error) {
			this.close();
			throw error;
		}
	}
	private async openMic(sender: RTCRtpSender): Promise<void> {
		if (this.stream) return;
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({
				audio: {echoCancellation: true, noiseSuppression: true, autoGainControl: true},
			});
		} catch (error) {
			const name = error instanceof DOMException ? error.name : '';
			if (!this.closed) this.callbacks.onError(name === 'NotFoundError' ? 'mic-missing' : 'mic-denied');
			throw error;
		}
		if (this.closed) {
			for (const t of stream.getTracks()) t.stop();
			throw new Error('closed');
		}
		this.stream = stream;
		this.audio.context?.createMediaStreamSource(stream).connect(this.mic!);
		const track = stream.getAudioTracks()[0];
		if (track) await sender.replaceTrack(track);
	}
	/** The server starts the user's Codex voice session and answers the offer. */
	private async requestSession(sdp: string | undefined): Promise<string> {
		const response = await http.post<StartReply>(Endpoints.KONVO_ASSISTANT_VOICE_CODEX_START, {
			body: {
				mode: this.callbacks.mode,
				sdp,
				channel_id: this.callbacks.channelId,
				voice: this.callbacks.voice,
				screen: this.callbacks.context?.(),
				send_delay: this.callbacks.sendDelay,
			},
		});
		if (!response.body?.sdp || !response.body.id) throw new Error('No Codex voice session');
		this.id = response.body.id;
		if (this.closed) {
			this.stopServer();
			throw new Error('closed');
		}
		return response.body.sdp;
	}
	private rms(analyser: AnalyserNode | null): number {
		if (!analyser) return 0;
		analyser.getFloatTimeDomainData(this.samples);
		return Math.sqrt(this.samples.reduce((sum, n) => sum + n * n, 0) / this.samples.length);
	}
	private setState(state: LiveState) {
		if (state !== this.state) {
			this.state = state;
			this.callbacks.onState(state);
		}
	}
	private tick() {
		if (this.closed) return;
		const output = this.interrupted ? 0 : this.rms(this.output),
			mic = this.rms(this.mic),
			now = performance.now();
		if (mic > 0.025 && output <= 0.008) this.lastInput = now;
		if (this.waitingForAudio && this.answerAudioSeen && now - this.lastOutput > 1200) this.waitingForAudio = false;
		if (this.waitingForAudio && now - this.answerReadyAt > 60_000) {
			this.callbacks.onError('connection');
			this.close();
			return;
		}
		this.callbacks.onLevel(Math.min(1, (output > 0.008 ? output : mic) * 5));
		if (output > 0.008) {
			this.lastOutput = now;
			if (this.waitingForAudio) this.answerAudioSeen = true;
			this.setState('speaking');
		} else if (now - this.lastOutput < 500) this.setState('speaking');
		else if (this.backendPending || this.waitingForAudio) this.setState('thinking');
		else if (now - this.lastInput < 500) this.setState('hearing');
		else if (now - this.lastOutput > 1200) this.setState('listening');
	}
	/** Audio/data events are never treated as proof the workplace backend has completed. */
	private mediaEvent(event: Record<string, unknown>) {
		const type = String(event.type ?? '');
		if (type === 'input_audio_buffer.speech_started') {
			this.lastInput = performance.now();
			this.userText = '';
			this.assistantText = '';
			this.callbacks.onUserText('');
			this.callbacks.onAssistantText('');
			this.interrupted = false;
			this.backendPending = false;
			if (this.remoteAudio) this.remoteAudio.muted = false;
			this.setState('hearing');
		}
		if (type === 'input_audio_buffer.speech_stopped') {
			this.lastInput = performance.now();
			this.setState('thinking');
		}
	}
	/** Server transcripts are canonical and scoped to this authenticated session. */
	consume(event: VoiceEvent) {
		if (this.closed) return;
		if (event.type === 'backend_start') {
			this.backendPending = true;
			this.setState('thinking');
		}
		if (event.type === 'backend_done') {
			this.backendPending = false;
			if (event.text?.trim()) {
				this.waitingForAudio = true;
				this.answerAudioSeen = false;
				this.answerReadyAt = performance.now();
			}
		}
		if (event.type === 'transcript') {
			const text = event.text ?? '';
			if (event.role === 'user') {
				if (!event.done && this.userDone) {
					this.userText = '';
					this.assistantText = '';
					this.assistantDone = false;
					this.waitingForAudio = false;
					this.callbacks.onAssistantText('');
				}
				this.userDone = !!event.done;
				if (this.interrupted) {
					this.interrupted = false;
					if (this.remoteAudio) this.remoteAudio.muted = false;
				}
				this.userText = event.done ? text : this.userText + text;
				this.callbacks.onUserText(this.userText.trim());
				if (event.done) this.callbacks.onUserDone?.(this.userText.trim());
			} else if (event.role === 'assistant') {
				if (!event.done && this.assistantDone) this.assistantText = '';
				this.assistantDone = !!event.done;
				this.assistantText = event.done ? text : this.assistantText + text;
				this.callbacks.onAssistantText(this.assistantText.trim());
			}
		}
		if (event.type === 'error') {
			this.callbacks.onError('connection');
			this.close();
		}
		if (event.type === 'closed') {
			if (!this.closed) this.callbacks.onError('connection');
			this.close();
		}
	}
	private async poll() {
		if (this.closed || !this.id) return;
		try {
			const response = await http.post<{events: Array<VoiceEvent>; cursor: number; closed: boolean}>(
				Endpoints.KONVO_ASSISTANT_VOICE_CODEX_EVENTS,
				{body: {id: this.id, cursor: this.cursor}},
			);
			if (this.closed) return;
			if (!response.body) throw new Error('No voice events');
			for (const event of response.body.events) this.consume(event);
			this.cursor = response.body.cursor;
			if (response.body.closed) {
				this.callbacks.onError('connection');
				this.close();
				return;
			}
		} catch {
			if (!this.closed) {
				this.callbacks.onError('connection');
				this.close();
			}
		}
		if (!this.closed) this.pollTimer = setTimeout(() => void this.poll(), 1000);
	}
	setMuted(muted: boolean) {
		for (const track of this.stream?.getAudioTracks() ?? []) track.enabled = !muted;
	}
	interrupt() {
		this.interrupted = true;
		this.waitingForAudio = false;
		this.backendPending = false;
		if (this.remoteAudio) this.remoteAudio.muted = true;
		this.lastOutput = Number.NEGATIVE_INFINITY;
		this.setState('listening');
	}
	private stopServer() {
		const id = this.id;
		this.id = null;
		if (id) void http.post(Endpoints.KONVO_ASSISTANT_VOICE_CODEX_STOP, {body: {id}}).catch(() => {});
	}
	close() {
		if (this.closed) return;
		this.closed = true;
		if (this.pollTimer) clearTimeout(this.pollTimer);
		if (this.ticker) clearInterval(this.ticker);
		this.pollTimer = null;
		this.ticker = null;
		this.remoteAudio?.pause();
		if (this.remoteAudio) this.remoteAudio.srcObject = null;
		this.remoteAudio = null;
		this.channel?.close();
		this.peer?.close();
		for (const t of this.stream?.getTracks() ?? []) t.stop();
		this.stream = null;
		this.mic?.disconnect();
		this.output?.disconnect();
		this.gain?.disconnect();
		this.stopServer();
		// VoiceModeSession owns the context on success so its closing chime can finish.
		if (!this.started && !this.primedContext) void this.audio.context?.close().catch(() => {});
	}
}
